import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface SavedView { name: string; search: Record<string, unknown> }
export interface TablePrefs { hidden: string[]; density: 'comfortable' | 'compact'; pageSize?: number; views: SavedView[] }

const EMPTY: TablePrefs = { hidden: [], density: 'comfortable', views: [] };

/**
 * A person's own settings for one list — columns shown, density, page size, saved views. Kept on the server, so they
 * follow the person to any computer. Without a table id, nothing is stored and the defaults apply.
 */
/** `defaultHidden`: columns hidden until the person makes their own choice (long lists show their most used columns). */
export function useTablePrefs(tableId: string | undefined, defaultHidden: string[] = []) {
  const qc = useQueryClient();
  const key = ['table-prefs', tableId];
  const { data, isFetched } = useQuery({ queryKey: key, queryFn: () => api<TablePrefs | null>(`/me/table-prefs/${tableId}`), enabled: !!tableId, staleTime: Infinity });
  const prefs: TablePrefs = data ? { ...EMPTY, ...data } : { ...EMPTY, hidden: defaultHidden };
  const save = useMutation({
    mutationFn: (next: TablePrefs) => api<TablePrefs>(`/me/table-prefs/${tableId}`, { method: 'PUT', body: next }),
    onMutate: (next) => qc.setQueryData(key, next),
  });
  return { prefs, ready: !tableId || isFetched, save: (patch: Partial<TablePrefs>) => tableId && save.mutate({ ...prefs, ...patch }) };
}
